import { forwardRef, type CSSProperties } from 'react';
import RDP from 'react-datepicker';
import dayjs, { type Dayjs } from 'dayjs';
import { Clock, X } from 'lucide-react';
import { cn } from './cn';

/** Ant Design `TimePicker` replacement (Dayjs value in/out), on react-datepicker. */
const SIZE: Record<string, string> = { small: 'h-8 text-[13px]', middle: 'h-9 text-sm', large: 'h-11 text-[15px]' };

const TimeInput = forwardRef<HTMLButtonElement, any>(function TimeInput(
  { value, onClick, onClear, placeholder, size = 'middle', disabled, allowClear }, ref,
) {
  return (
    <button
      type="button" ref={ref} onClick={onClick} disabled={disabled}
      className={cn('inline-flex w-full min-w-[7rem] items-center gap-2 rounded-control border border-line bg-surface px-3 text-left text-content transition-colors hover:border-brand-300 focus-visible:border-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60', SIZE[size])}
    >
      <Clock className="size-4 shrink-0 text-subtle" />
      <span className={cn('flex-1 truncate', !value && 'text-subtle')}>{value || placeholder}</span>
      {allowClear && value && (
        <span role="button" tabIndex={-1} aria-label="Clear" onClick={(e) => { e.stopPropagation(); onClear?.(); }} className="grid size-5 place-items-center rounded-full text-subtle hover:bg-surface-2 hover:text-content">
          <X className="size-3.5" />
        </span>
      )}
    </button>
  );
});

export interface TimePickerProps {
  value?: Dayjs | null;
  onChange?: (d: Dayjs | null, timeString: string) => void;
  format?: string;
  use12Hours?: boolean;
  minuteStep?: number;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  className?: string;
  style?: CSSProperties;
  size?: 'small' | 'middle' | 'large';
}

export function TimePicker({
  value, onChange, format = 'HH:mm', use12Hours, minuteStep = 1, placeholder = 'Select time',
  disabled, allowClear = true, className, style, size = 'middle',
}: TimePickerProps) {
  const twelve = use12Hours || /[Ah]/.test(format);
  const df = twelve ? 'h:mm aa' : 'HH:mm'; // react-datepicker display (date-fns tokens)
  const emitFmt = twelve ? 'hh:mm A' : 'HH:mm'; // dayjs tokens for the emitted string
  return (
    <div className={cn('gb-timepicker', className)} style={style}>
      <RDP
        selected={value ? value.toDate() : null}
        onChange={(d: Date | null) => onChange?.(d ? dayjs(d) : null, d ? dayjs(d).format(emitFmt) : '')}
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={minuteStep}
        timeCaption="Time"
        dateFormat={df}
        disabled={disabled}
        placeholderText={placeholder}
        popperClassName="z-[var(--z-popover)]"
        customInput={<TimeInput size={size} disabled={disabled} allowClear={allowClear} onClear={() => onChange?.(null, '')} />}
      />
    </div>
  );
}
